import { useState, useCallback, useEffect } from 'react';
import { Node, Edge, useNodesState, useEdgesState, addEdge } from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/contexts/AuthContext';
import { useUndoRedo } from './useUndoRedo';

import { useUserPreferences } from './useUserPreferences';
import { auditService } from '@/services/auditService';

const initialNodes: Node[] = [];
const initialEdges: Edge[] = [];

const useProductionFlowState = () => {
  const { user } = useAuth();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [colorPickerValue, setColorPickerValue] = useState('#ff0072');
  const [selectedCategory, setSelectedCategory] = useState('shapes');
  const [selectedShape, setSelectedShape] = useState('rectangle');
  const [currentWhiteboardId, setCurrentWhiteboardId] = useState<string | null>(null);
  const [currentWhiteboardName, setCurrentWhiteboardName] = useState<string | null>(null);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(null);
  const [currentWorkspaceName, setCurrentWorkspaceName] = useState<string | null>('Personal Workspace');
  const [isCollaborationEnabled, setIsCollaborationEnabled] = useState(false);
  const [collaborators, setCollaborators] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [userWhiteboards, setUserWhiteboards] = useState<any[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const { preferences } = useUserPreferences();
  const { saveState, undo, redo, canUndo, canRedo } = useUndoRedo(nodes, edges);

  const handleColorChange = useCallback((color: string) => {
    setColorPickerValue(color);
  }, []);

  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category);
  }, []);

  const handleShapeChange = useCallback((shape: string) => {
    setSelectedShape(shape);
    // Also apply to the currently selected node, if any
    setNodes((nds) =>
      nds.map((n) =>
        selectedNodeId && n.id === selectedNodeId
          ? { ...n, data: { ...(n.data || {}), shape } }
          : n
      )
    );
  }, [setNodes, selectedNodeId]);

  const addNewNode = useCallback(() => {
    const id = uuidv4();
    const newNode = {
      id,
      type: 'customNode',
      position: { x: 250, y: 250 },
      data: { label: `${selectedShape}`, color: colorPickerValue, shape: selectedShape },
    };
    setNodes((nds) => nds.concat(newNode));
    auditService.log({
      action: 'add_node',
      resource_type: 'node',
      resource_id: id,
      metadata: { nodeType: selectedShape, color: colorPickerValue }
    });
  }, [setNodes, selectedShape, colorPickerValue]);

  const onConnect = useCallback(
    (params: any) => {
      setEdges((eds) => addEdge(params, eds));
      auditService.log({
        action: 'create_edge',
        resource_type: 'edge',
        resource_id: `${params.source}-${params.target}`,
        metadata: { source: params.source, target: params.target }
      });
    },
    [setEdges]
  );

  const onNodeClick = useCallback(
    (event: any, node: any) => {
      setSelectedNodeId(node.id);
      setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === node.id })));
    },
    [setNodes]
  );

  const onEdgeClick = useCallback((event: any, edge: any) => {
    console.log('Edge clicked', edge);
  }, []);

  const handleChangeAllNodesColor = useCallback((color: string) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.data) {
          return {
            ...node,
            data: {
              ...node.data,
              color: color,
            },
          };
        } else {
          return node;
        }
      })
    );
    auditService.log({
      action: 'change_all_node_colors',
      resource_type: 'node',
      resource_id: 'all',
      metadata: { color }
    });
  }, [setNodes]);

  const handleChangeWhiteboardName = useCallback(async (whiteboardId: string, newName: string) => {
    try {
      setIsSaving(true);
      // For now using a simple update - can be enhanced with actual service call
      setCurrentWhiteboardName(newName);
      setUserWhiteboards(prevWhiteboards =>
        prevWhiteboards.map(wb =>
          wb.id === whiteboardId ? { ...wb, name: newName } : wb
        )
      );
      auditService.log({
        action: 'update_whiteboard_name',
        resource_type: 'whiteboard',
        resource_id: whiteboardId,
        metadata: { newName }
      });
    } catch (error) {
      console.error("Error updating whiteboard name:", error);
    } finally {
      setIsSaving(false);
    }
  }, [setCurrentWhiteboardName, setUserWhiteboards]);

  const handleSaveWhiteboard = useCallback(async () => {
    if (!currentWhiteboardId) {
      console.warn("No whiteboard selected to save.");
      return;
    }

    try {
      setIsSaving(true);
      // For now just logging - can be enhanced with actual save logic
      console.log("Saving whiteboard:", currentWhiteboardId);
      auditService.log({
        action: 'save_whiteboard',
        resource_type: 'whiteboard',
        resource_id: currentWhiteboardId
      });
    } catch (error) {
      console.error("Error saving whiteboard:", error);
    } finally {
      setIsSaving(false);
    }
  }, [nodes, edges, currentWhiteboardId]);

  const handleLoadWhiteboard = useCallback(async (whiteboardId: string) => {
    setIsLoading(true);
    try {
      // For now just setting basic data - can be enhanced with actual load logic
      setCurrentWhiteboardId(whiteboardId);
      setCurrentWhiteboardName("Loaded Whiteboard");
      auditService.log({
        action: 'load_whiteboard',
        resource_type: 'whiteboard',
        resource_id: whiteboardId
      });
    } catch (error) {
      console.error("Error loading whiteboard:", error);
    } finally {
      setIsLoading(false);
    }
  }, [setNodes, setEdges, setCurrentWhiteboardId, setCurrentWhiteboardName]);

  const handleCreateNewWhiteboard = useCallback(async () => {
    setIsSaving(true);
    try {
      const newWhiteboardId = uuidv4();
      const newWhiteboard = {
        id: newWhiteboardId,
        name: 'New Whiteboard',
        user_id: user?.id || 'defaultUser'
      };
      setCurrentWhiteboardId(newWhiteboard.id);
      setCurrentWhiteboardName(newWhiteboard.name);
      setNodes([]);
      setEdges([]);
      setUserWhiteboards(prevWhiteboards => [...prevWhiteboards, newWhiteboard]);
      auditService.log({
        action: 'create_whiteboard',
        resource_type: 'whiteboard',
        resource_id: newWhiteboard.id,
        metadata: { name: newWhiteboard.name }
      });
    } catch (error) {
      console.error("Error creating new whiteboard:", error);
    } finally {
      setIsSaving(false);
    }
  }, [setNodes, setEdges, setCurrentWhiteboardId, setCurrentWhiteboardName, user, setUserWhiteboards]);

  const handleUndo = useCallback(() => {
    const previousState = undo();
    if (previousState) {
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
      auditService.log({
        action: 'undo',
        resource_type: 'whiteboard',
        resource_id: currentWhiteboardId || undefined
      });
    }
  }, [undo, currentWhiteboardId]);

  const handleRedo = useCallback(() => {
    const nextState = redo();
    if (nextState) {
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      auditService.log({
        action: 'redo',
        resource_type: 'whiteboard',
        resource_id: currentWhiteboardId || undefined
      });
    }
  }, [redo, currentWhiteboardId]);

  // Save state when nodes or edges change (for undo/redo)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveState(nodes, edges);
    }, 500); // Debounce state saving

    return () => clearTimeout(timeoutId);
  }, [nodes, edges, saveState]);

  // Note: Keyboard shortcuts moved to component level to avoid hook context issues

  useEffect(() => {
    const fetchUserWhiteboards = async () => {
      if (user?.id) {
        setIsLoading(true);
        try {
          // For now just setting empty array - can be enhanced with actual fetch
          setUserWhiteboards([]);
        } catch (error) {
          console.error("Error fetching user whiteboards:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchUserWhiteboards();
  }, [user?.id]);

  return {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onNodeClick,
    onEdgeClick,
    colorPickerValue,
    selectedCategory,
    selectedShape,
    handleColorChange,
    handleCategoryChange,
    handleShapeChange,
    addNewNode,
    currentWhiteboardId,
    currentWhiteboardName,
    handleChangeWhiteboardName,
    isCollaborationEnabled,
    toggleCollaboration: () => setIsCollaborationEnabled(!isCollaborationEnabled),
    collaborators,
    handleChangeAllNodesColor,
    isLoading,
    isSaving,
    handleSaveWhiteboard,
    handleLoadWhiteboard,
    handleCreateNewWhiteboard,
    userWhiteboards,
    canUndo,
    canRedo,
    undo: handleUndo,
    redo: handleRedo
  };
};

export default useProductionFlowState;
